import styled, { css } from 'styled-components';

import { CheckboxRadio } from 'shared/components/CheckboxRadio';
import { Loader } from 'shared/components/Loader';
import { Choice } from 'shared/components/Choice';
import { QuestionFormType } from 'shared/enums/QuestionFormType';
import { variableFontRegular } from 'styles/theme/text';

// TODO: remove choiceClassName and checkboxClassName when remove
// temp_web_enable_updated_fit_modal
interface IQuestionForm {
  checked: number | string;
  choiceClassName?: string;
  checkboxClassName?: string;
  className?: string;
  'data-qa': string;
  'data-qa-prefix'?: string; // Automation doesnt like values starting with numbers.
  error?: string;
  gridStyle?: { [key: string]: string };
  invertChecked?: boolean;
  isGrid?: boolean;
  isLoading?: boolean;
  name: string;
  onChange: (e) => void;
  options: { label: string; value: number | string }[];
  question: string;
  darkTheme?: boolean;
  type?: QuestionFormType;
  withBorders?: boolean;
}

export const QuestionForm = ({
  checked,
  className,
  choiceClassName = '',
  checkboxClassName = '',
  'data-qa': dataQa,
  'data-qa-prefix': dataQaPrefix = '',
  error,
  gridStyle,
  invertChecked = false,
  isGrid = false,
  isLoading = false,
  name,
  onChange,
  options,
  question,
  darkTheme,
  type = QuestionFormType.CHOICE,
  withBorders = false,
}: IQuestionForm) => {
  return (
    <QuestionForm.Wrapper data-qa={`${dataQa}_question_form`} className={className}>
      <QuestionForm.Question>{question}</QuestionForm.Question>
      {isLoading ? (
        <QuestionForm.Loader>
          <Loader />
        </QuestionForm.Loader>
      ) : (
        <QuestionForm.RadioOptions data-qa={`${dataQa}_options`} $isGrid={isGrid} style={gridStyle}>
          {options.map((option) =>
            type === QuestionFormType.CHOICE ? (
              <Choice
                checked={String(checked) === String(option.value)}
                className={choiceClassName}
                key={option.value}
                invertChecked={invertChecked}
                label={option.label}
                onChange={onChange}
                name={name}
                type="radio"
                value={option.value}
                data-qa={`${dataQaPrefix}${option.value}_${dataQa}_choice`}
                darkTheme={darkTheme}
                withBorders={withBorders}
              />
            ) : (
              <QuestionForm.CheckboxRadio
                data-qa={`${dataQaPrefix}${option.value}_${dataQa}_checkbox_radio`}
                checked={String(checked) === String(option.value)}
                className={checkboxClassName}
                label={option.label}
                name={name}
                onChange={onChange}
                type="checkbox"
                value={option.value}
              />
            ),
          )}
        </QuestionForm.RadioOptions>
      )}
      {error && <QuestionForm.Error>{error}</QuestionForm.Error>}
    </QuestionForm.Wrapper>
  );
};

QuestionForm.Loader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 105px;
`;

QuestionForm.Wrapper = styled.fieldset`
  border: none;
  padding: 0;
  margin-bottom: 24px;
`;

QuestionForm.Question = styled.legend`
  margin-bottom: 16px;
  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    ${variableFontRegular({ height: '14px', size: '11.5px' })}
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ height: '14px', size: '11.5px' })}
  }
`;

QuestionForm.RadioOptions = styled.div<{ $isGrid: IQuestionForm['isGrid'] }>`
  width: 100%;

  ${({ $isGrid }) =>
    $isGrid
      ? css`
          display: grid;
          grid-gap: 16px;
          grid-template-columns: repeat(7, 1fr);
        `
      : css`
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 16px;
        `}
`;

QuestionForm.Error = styled.div`
  color: ${({ theme }) => theme.colors.red};
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
  margin: 12px 0 0;
`;

QuestionForm.CheckboxRadio = styled(CheckboxRadio)`
  flex-direction: row-reverse;
  justify-content: start;

  > span {
    margin-right: 8px;
    margin-bottom: 1px;
  }
`;
